import React, { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { fetchVideos, fetchCategories } from "../lib/api";
import VideoCard from "../components/VideoCard";

const PAGE_SIZE = 9;

export default function VideosCatalog() {
    const [videos, setVideos] = useState([]);
    const [categories, setCategories] = useState([]);
    const [activeCat, setActiveCat] = useState("All");
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(true);
    const [visible, setVisible] = useState(PAGE_SIZE);

    useEffect(() => {
        fetchCategories().then((d) => setCategories(d.categories || []));
    }, []);

    useEffect(() => {
        setLoading(true);
        setVisible(PAGE_SIZE);
        const params = {};
        if (activeCat !== "All") params.category = activeCat;
        if (search.trim()) params.search = search.trim();
        const handle = setTimeout(() => {
            fetchVideos(params)
                .then(setVideos)
                .finally(() => setLoading(false));
        }, 250);
        return () => clearTimeout(handle);
    }, [activeCat, search]);

    const cats = useMemo(() => ["All", ...categories], [categories]);
    const shown = videos.slice(0, visible);
    const hasMore = visible < videos.length;

    return (
        <div data-testid="videos-page">
            <section className="pt-36 pb-16 max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
                <p className="text-[11px] uppercase tracking-[0.3em] text-tattva-muted mb-6">
                    Library
                </p>
                <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[0.95] text-tattva-ink max-w-4xl">
                    Spiritual cinema{" "}
                    <span className="italic text-tattva-terracotta">
                        & wisdom
                    </span>
                    .
                </h1>
                <p className="mt-8 max-w-2xl text-base text-tattva-muted leading-relaxed">
                    A curated catalogue of short films, essays and lectures on
                    Hindu philosophy, festivals and the science of sacred
                    ritual.
                </p>
            </section>

            <section className="border-y border-tattva-ornament sticky top-20 z-20 backdrop-blur-xl bg-[#FDFBF7]/90">
                <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-5 flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
                    <div
                        className="flex gap-2 md:gap-6 overflow-x-auto"
                        data-testid="category-filter-bar"
                    >
                        {cats.map((c) => (
                            <button
                                key={c}
                                onClick={() => setActiveCat(c)}
                                data-testid={`category-btn-${c.toLowerCase().replace(/\s+/g, "-")}`}
                                className={`whitespace-nowrap text-[11px] uppercase tracking-[0.25em] transition-colors pb-1 border-b ${
                                    activeCat === c
                                        ? "text-tattva-terracotta border-tattva-terracotta"
                                        : "text-tattva-muted border-transparent hover:text-tattva-ink"
                                }`}
                            >
                                {c}
                            </button>
                        ))}
                    </div>
                    <div className="flex-1 flex items-center gap-3 border-b border-tattva-ornament focus-within:border-tattva-terracotta transition-colors md:max-w-md md:ml-auto">
                        <Search size={16} className="text-tattva-muted" />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search shlokas, festivals, rituals…"
                            data-testid="video-search-input"
                            className="w-full bg-transparent outline-none py-3 text-sm text-tattva-ink placeholder:text-tattva-muted"
                        />
                    </div>
                </div>
            </section>

            <section className="py-20 max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 md:gap-14">
                        {Array.from({ length: 6 }).map((_, i) => (
                            <div key={i} className="animate-pulse">
                                <div className="aspect-video bg-tattva-surface" />
                                <div className="h-3 w-24 bg-tattva-surface mt-5" />
                                <div className="h-6 w-3/4 bg-tattva-surface mt-3" />
                            </div>
                        ))}
                    </div>
                ) : videos.length === 0 ? (
                    <p
                        data-testid="videos-empty"
                        className="font-serif italic text-2xl text-tattva-muted text-center py-20"
                    >
                        No films found in this silence. Try another word.
                    </p>
                ) : (
                    <>
                        <div
                            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 md:gap-14"
                            data-testid="videos-grid"
                        >
                            {shown.map((v) => (
                                <VideoCard key={v.id} video={v} />
                            ))}
                        </div>
                        <div className="flex flex-col items-center mt-20 gap-4">
                            <p className="text-[11px] uppercase tracking-[0.3em] text-tattva-muted">
                                Showing {shown.length} of {videos.length}
                            </p>
                            {hasMore && (
                                <button
                                    onClick={() =>
                                        setVisible((v) => v + PAGE_SIZE)
                                    }
                                    data-testid="explore-more-btn"
                                    className="border border-tattva-ink text-tattva-ink hover:bg-tattva-ink hover:text-tattva-bg px-10 py-4 uppercase tracking-[0.22em] text-xs transition-colors"
                                >
                                    Explore more
                                </button>
                            )}
                        </div>
                    </>
                )}
            </section>
        </div>
    );
}
